"use client";

import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { MapPin, Phone, Mail, Clock, Send, CircleAlert as AlertCircle, CircleCheck as CheckCircle2, Building } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
declare const wvcClient: any;
const getWvcClient = () => typeof wvcClient !== "undefined" ? wvcClient : (window as any).wvcClient;
interface FormFieldDefinition {
  id: string;
  key: string;
  type: string;
  label: string;
  placeholder?: string;
  required?: boolean;
  validation?: {
    messages?: {
      required?: string;
      invalid?: string;
    };
  };
}
interface FormDefinition {
  id: number;
  key: string;
  name: string;
  fields: FormFieldDefinition[];
  spam: {
    token: string;
    honeypot?: {
      field: string;
    } | null;
    min_seconds?: number;
  };
}
export default function Contact() {
  const shouldReduceMotion = useReducedMotion();

  // Form definition state
  const [formDef, setFormDef] = useState<FormDefinition | null>(null);
  const [loadStatus, setLoadStatus] = useState<"loading" | "ready" | "error">("loading");
  const [fetchTimestamp, setFetchTimestamp] = useState<number>(0);

  // Field values and validation errors (keyed by field id)
  const [values, setValues] = useState<Record<string, string>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, {
    message?: string;
  }>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [confirmationHtml, setConfirmationHtml] = useState<string | null>(null);

  // Honeypot trap state
  const [hpValue, setHpValue] = useState<string>("");

  // Fetch form definition on mount
  useEffect(() => {
    let isMounted = true;
    const client = getWvcClient();
    if (!client?.pluginRequest) {
      // Fallback state if client is temporarily unavailable
      setLoadStatus("error");
      return;
    }
    client.pluginRequest("wvc-forms/v1/forms/contact_form").then((data: FormDefinition) => {
      if (!isMounted) return;
      setFormDef(data);
      setFetchTimestamp(Date.now());
      setLoadStatus("ready");

      // Initialize empty string state for input fields
      const initialVals: Record<string, string> = {};
      if (data.fields && Array.isArray(data.fields)) {
        data.fields.forEach(f => {
          initialVals[f.id] = "";
        });
      }
      setValues(initialVals);
    }).catch((err: any) => {
      if (!isMounted) return;
      if (err?.status === 404) {
        // Recovery attempt per guidelines
        client.pluginRequest("wvc-forms/v1/forms").then((listData: {
          items?: Array<{
            key: string;
            name: string;
          }>;
        }) => {
          if (!isMounted) return;
          const items = listData?.items || [];
          const matched = items.find(i => i.key === "contact_form") || (items.length === 1 ? items[0] : null) || items.find(i => i.name.toLowerCase().includes("contact") || i.name.toLowerCase().includes("liên hệ"));
          if (matched) {
            return client.pluginRequest(`wvc-forms/v1/forms/${matched.key}`);
          }
          throw new Error("Form not found");
        }).then((recoveredForm: FormDefinition) => {
          if (!isMounted) return;
          setFormDef(recoveredForm);
          setFetchTimestamp(Date.now());
          setLoadStatus("ready");
          const initialVals: Record<string, string> = {};
          if (recoveredForm?.fields) {
            recoveredForm.fields.forEach(f => {
              initialVals[f.id] = "";
            });
          }
          setValues(initialVals);
        }).catch(() => {
          if (isMounted) setLoadStatus("error");
        });
      } else {
        setLoadStatus("error");
      }
    });
    return () => {
      isMounted = false;
    };
  }, []);
  const handleInputChange = (fieldId: string, val: string) => {
    setValues(prev => ({
      ...prev,
      [fieldId]: val
    }));
    if (fieldErrors[fieldId]) {
      setFieldErrors(prev => {
        const next = {
          ...prev
        };
        delete next[fieldId];
        return next;
      });
    }
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formDef || isSubmitting) return;
    setFormError(null);
    setFieldErrors({});

    // Enforce spam.min_seconds rule to prevent silent quarantine
    const minSeconds = formDef.spam?.min_seconds ?? 3;
    const elapsedSeconds = (Date.now() - fetchTimestamp) / 1000;
    if (elapsedSeconds < minSeconds) {
      await new Promise(resolve => setTimeout(resolve, Math.ceil((minSeconds - elapsedSeconds) * 1000)));
    }
    setIsSubmitting(true);
    const client = getWvcClient();
    try {
      const payload: Record<string, any> = {
        values,
        token: formDef.spam.token,
        meta: {
          source_url: typeof window !== "undefined" ? window.location.href : "",
          referrer: typeof document !== "undefined" ? document.referrer : ""
        }
      };
      if (formDef.spam.honeypot?.field) {
        payload.hp = {
          [formDef.spam.honeypot.field]: hpValue
        };
      }
      const res = await client.pluginRequest(`wvc-forms/v1/forms/${formDef.key}/submit`, {
        method: "POST",
        data: payload
      });
      if (res?.success) {
        if (res.confirmation?.type === "redirect" && res.confirmation?.url) {
          window.location.assign(res.confirmation.url);
        } else {
          setConfirmationHtml(res.confirmation?.message || "<p>Cảm ơn quý vị đã gửi thông tin. Ban Thư ký Hội Địa lý môi trường Việt Nam sẽ phản hồi trong thời gian sớm nhất.</p>");
        }
      } else if (res?.code === "frms_validation_failed") {
        setFieldErrors(res.errors || {});
      } else {
        setFormError(res?.message || "Đã xảy ra lỗi trong quá trình gửi. Vui lòng thử lại.");
      }
    } catch (err: any) {
      setFormError("Không thể hoàn tất kết nối máy chủ. Vui lòng kiểm tra lại mạng hoặc thử lại sau.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Helper to match fields with fallbacks for standard contact properties
  const getFieldByRole = useCallback((roleKey: string) => {
    if (!formDef?.fields) return null;
    return formDef.fields.find(f => f.key === roleKey) || formDef.fields.find(f => f.key.toLowerCase().includes(roleKey)) || null;
  }, [formDef]);
  const nameField = getFieldByRole("name") || getFieldByRole("fullname");
  const emailField = getFieldByRole("email");
  const phoneField = getFieldByRole("phone");
  const subjectField = getFieldByRole("subject");
  const messageField = getFieldByRole("message") || getFieldByRole("content");
  const otherFields = (formDef?.fields || []).filter(f => f.id !== nameField?.id && f.id !== emailField?.id && f.id !== phoneField?.id && f.id !== subjectField?.id && f.id !== messageField?.id);
  return <section id="thông-tin-liên-hệ-bản-đồ-và-form-gửi-tin-nhắn" data-nav="light" className="bg-background text-foreground py-16 lg:py-24 border-b border-border transition-colors relative">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <motion.div initial={shouldReduceMotion ? false : {
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.38,
        ease: [0.16, 1, 0.3, 1]
      }} className="mb-12 max-w-3xl">
          <div className="flex items-center gap-2 mb-3">
            <span className="h-0.5 w-8 bg-secondary block" />
            <span className="text-xs md:text-sm font-semibold text-secondary uppercase tracking-widest">
              Thông tin liên hệ chính thức
            </span>
          </div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-secondary font-default">
            Thông tin liên hệ, bản đồ và form gửi tin nhắn
          </h1>
          <p className="mt-3 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Hội Địa lý môi trường Việt Nam (VAEG) tiếp nhận thông tin khoa học, đề xuất hợp tác và ý kiến đóng góp từ các cơ quan, tổ chức và cá nhân trên toàn quốc.
          </p>
        </motion.div>

        {/* 50/50 Asymmetric Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start">
          {/* Left Column: Contact Card & Map */}
          <motion.div initial={shouldReduceMotion ? false : {
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.38,
          delay: 0.08,
          ease: [0.16, 1, 0.3, 1]
        }} className="flex flex-col gap-6">
            {/* Contact Details Card */}
            <Card className="border border-border bg-card rounded-md shadow-sm overflow-hidden p-6 sm:p-8">
              <div className="flex items-center gap-3 pb-6 border-b border-border">
                <div className="size-11 rounded-md bg-accent flex items-center justify-center text-primary shrink-0">
                  <Building className="size-5" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-foreground font-default">
                    Trụ sở Hội Địa lý môi trường Việt Nam
                  </h2>
                  <p className="text-xs text-muted-foreground">
                    Theo Quyết định thành lập số 483/QĐ-BNV của Bộ Nội vụ
                  </p>
                </div>
              </div>

              <div className="divide-y divide-border/60">
                <div className="py-4 flex items-start gap-4" data-index={0}>
                  <div className="size-9 rounded-md bg-accent/60 flex items-center justify-center text-primary shrink-0 mt-0.5">
                    <MapPin className="size-4" />
                  </div>
                  <div>
                    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider block">
                      Địa chỉ trụ sở (Hà Nội)
                    </span>
                    <span className="text-sm font-semibold text-foreground mt-0.5 block">
                      Số 129 Võ Chí Công, phường Tây Hồ, thành phố Hà Nội
                    </span>
                  </div>
                </div>

                <div className="py-4 flex items-start gap-4" data-index={1}>
                  <div className="size-9 rounded-md bg-accent/60 flex items-center justify-center text-primary shrink-0 mt-0.5">
                    <Phone className="size-4" />
                  </div>
                  <div>
                    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider block">
                      Số điện thoại
                    </span>
                    <span className="text-sm font-semibold text-foreground mt-0.5 block">
                      +84 912863391
                    </span>
                  </div>
                </div>

                <div className="py-4 flex items-start gap-4" data-index={2}>
                  <div className="size-9 rounded-md bg-accent/60 flex items-center justify-center text-primary shrink-0 mt-0.5">
                    <Mail className="size-4" />
                  </div>
                  <div>
                    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider block">
                      Email tiếp nhận
                    </span>
                    <span className="text-sm font-semibold text-foreground mt-0.5 block">
                      contact@vaegs.com
                    </span>
                  </div>
                </div>

                <div className="py-4 flex items-start gap-4" data-index={3}>
                  <div className="size-9 rounded-md bg-accent/60 flex items-center justify-center text-primary shrink-0 mt-0.5">
                    <Clock className="size-4" />
                  </div>
                  <div>
                    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider block">
                      Giờ làm việc hành chính
                    </span>
                    <span className="text-sm font-semibold text-foreground mt-0.5 block">
                      Thứ Hai - Thứ Sáu: 08:00 - 17:00
                    </span>
                    <span className="text-sm font-semibold text-foreground mt-0.5 block">
                      Nghỉ Thứ Bảy, Chủ Nhật và ngày lễ
                    </span>
                  </div>
                </div>
              </div>
            </Card>

            {/* Hanoi Interactive Map Card */}
            <Card className="border border-border bg-card rounded-md shadow-sm overflow-hidden flex flex-col">
              <div className="px-6 py-4 border-b border-border bg-muted/40 flex items-center justify-between">
                <span className="text-xs font-semibold text-foreground uppercase tracking-wider flex items-center gap-2">
                  <MapPin className="size-3.5 text-primary" />
                  Khu vực hành chính Hà Nội
                </span>
                <span className="text-xs text-muted-foreground">Bản đồ định vị</span>
              </div>
              <div className="w-full h-72 sm:h-80 relative bg-muted/30">
                <iframe title="Bản đồ trụ sở Hội Địa lý môi trường Việt Nam" src="https://www.google.com/maps?q=S%E1%BB%91%20129%20V%C3%B5%20Ch%C3%AD%20C%C3%B4ng%2C%20ph%C6%B0%E1%BB%9Dng%20T%C3%A2y%20H%E1%BB%93%2C%20th%C3%A0nh%20ph%E1%BB%91%20H%C3%A0%20N%E1%BB%99i&output=embed" width="100%" height="100%" style={{
                border: 0
              }} allowFullScreen={false} loading="lazy" referrerPolicy="no-referrer-when-downgrade" className="w-full h-full grayscale contrast-[0.95] hover:grayscale-0 transition-all duration-300" />
              </div>
            </Card>
          </motion.div>

          {/* Right Column: Official Inquiry Form */}
          <motion.div initial={shouldReduceMotion ? false : {
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.38,
          delay: 0.16,
          ease: [0.16, 1, 0.3, 1]
        }}>
            <Card className="border border-border bg-card rounded-md shadow-sm p-6 sm:p-8">
              <div className="mb-6">
                <h3 className="text-xl font-semibold text-foreground font-default">
                  Gửi thư liên hệ trực tuyến
                </h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  Vui lòng điền đầy đủ các thông tin bên dưới để Ban Thư ký Hội tiếp nhận và phản hồi văn bản chính xác.
                </p>
              </div>

              {confirmationHtml ? <div className="rounded-md bg-accent/60 border border-primary/20 p-6 flex flex-col items-center text-center">
                  <div className="size-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center mb-4">
                    <CheckCircle2 className="size-6" />
                  </div>
                  <h4 className="text-lg font-semibold text-foreground mb-2 font-default">
                    Gửi thư thành công
                  </h4>
                  <div className="text-sm text-foreground leading-relaxed prose max-w-none" dangerouslySetInnerHTML={{
                __html: confirmationHtml
              }} />
                  <Button variant="outline" className="mt-6 text-sm" onClick={() => {
                setConfirmationHtml(null);
                const resetVals: Record<string, string> = {};
                if (formDef?.fields) {
                  formDef.fields.forEach(f => {
                    resetVals[f.id] = "";
                  });
                }
                setValues(resetVals);
              }}>
                    Gửi thư liên hệ khác
                  </Button>
                </div> : loadStatus === "error" ? <div className="rounded-md border border-border bg-muted/40 p-6 text-center">
                  <AlertCircle className="size-8 text-destructive mx-auto mb-2" />
                  <p className="text-sm font-semibold text-foreground">
                    Hệ thống tiếp nhận đang bảo trì
                  </p>
                  <p className="text-xs text-muted-foreground mt-1 mb-4">
                    Vui lòng liên hệ trực tiếp qua số điện thoại hoặc email trụ sở của Hội.
                  </p>
                </div> : <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Honeypot hidden input */}
                  {formDef?.spam?.honeypot?.field && <div style={{
                position: "absolute",
                left: "-9999px",
                top: "-9999px",
                opacity: 0,
                pointerEvents: "none"
              }} aria-hidden="true">
                      <input type="text" name={formDef.spam.honeypot.field} tabIndex={-1} autoComplete="off" value={hpValue} onChange={e => setHpValue(e.target.value)} />
                    </div>}

                  {/* Form Error Banner */}
                  {formError && <div className="p-3.5 rounded-md bg-destructive/10 border border-destructive/20 text-destructive text-sm flex items-start gap-2.5">
                      <AlertCircle className="size-4 mt-0.5 shrink-0" />
                      <span>{formError}</span>
                    </div>}

                  {/* Họ và tên (Full name) */}
                  <div className="space-y-1.5" data-index={0}>
                    <label htmlFor={nameField?.id || "f_name"} className="text-sm font-semibold text-foreground flex items-center justify-between">
                      <span>
                        Họ và tên
                        {nameField?.required && <span className="text-destructive ml-1">*</span>}
                      </span>
                    </label>
                    <Input id={nameField?.id || "f_name"} type="text" placeholder={nameField?.placeholder || "Nguyễn Văn A"} value={nameField ? values[nameField.id] || "" : ""} onChange={e => nameField && handleInputChange(nameField.id, e.target.value)} disabled={isSubmitting || loadStatus === "loading"} className="bg-card border-input focus:border-primary text-foreground" />
                    {nameField && fieldErrors[nameField.id]?.message && <p className="text-xs text-destructive mt-1 flex items-center gap-1">
                        <AlertCircle className="size-3" />
                        {fieldErrors[nameField.id]?.message}
                      </p>}
                  </div>

                  {/* Email & Phone in 2 Columns */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {/* Email */}
                    <div className="space-y-1.5" data-index={1}>
                      <label htmlFor={emailField?.id || "f_email"} className="text-sm font-semibold text-foreground flex items-center justify-between">
                        <span>
                          Email
                          {emailField?.required && <span className="text-destructive ml-1">*</span>}
                        </span>
                      </label>
                      <Input id={emailField?.id || "f_email"} type="email" placeholder={emailField?.placeholder || "diachi@email.vn"} value={emailField ? values[emailField.id] || "" : ""} onChange={e => emailField && handleInputChange(emailField.id, e.target.value)} disabled={isSubmitting || loadStatus === "loading"} className="bg-card border-input focus:border-primary text-foreground" />
                      {emailField && fieldErrors[emailField.id]?.message && <p className="text-xs text-destructive mt-1 flex items-center gap-1">
                          <AlertCircle className="size-3" />
                          {fieldErrors[emailField.id]?.message}
                        </p>}
                    </div>

                    {/* Số điện thoại (Phone) */}
                    <div className="space-y-1.5" data-index={2}>
                      <label htmlFor={phoneField?.id || "f_phone"} className="text-sm font-semibold text-foreground flex items-center justify-between">
                        <span>
                          Số điện thoại
                          {phoneField?.required && <span className="text-destructive ml-1">*</span>}
                        </span>
                      </label>
                      <Input id={phoneField?.id || "f_phone"} type="tel" placeholder={phoneField?.placeholder || "0912 345 678"} value={phoneField ? values[phoneField.id] || "" : ""} onChange={e => phoneField && handleInputChange(phoneField.id, e.target.value)} disabled={isSubmitting || loadStatus === "loading"} className="bg-card border-input focus:border-primary text-foreground" />
                      {phoneField && fieldErrors[phoneField.id]?.message && <p className="text-xs text-destructive mt-1 flex items-center gap-1">
                          <AlertCircle className="size-3" />
                          {fieldErrors[phoneField.id]?.message}
                        </p>}
                    </div>
                  </div>

                  {/* Chủ đề (Subject) */}
                  <div className="space-y-1.5" data-index={3}>
                    <label htmlFor={subjectField?.id || "f_subject"} className="text-sm font-semibold text-foreground flex items-center justify-between">
                      <span>
                        Chủ đề
                        {subjectField?.required && <span className="text-destructive ml-1">*</span>}
                      </span>
                    </label>
                    <Input id={subjectField?.id || "f_subject"} type="text" placeholder={subjectField?.placeholder || "Nội dung phản hồi hoặc liên hệ công tác"} value={subjectField ? values[subjectField.id] || "" : ""} onChange={e => subjectField && handleInputChange(subjectField.id, e.target.value)} disabled={isSubmitting || loadStatus === "loading"} className="bg-card border-input focus:border-primary text-foreground" />
                    {subjectField && fieldErrors[subjectField.id]?.message && <p className="text-xs text-destructive mt-1 flex items-center gap-1">
                        <AlertCircle className="size-3" />
                        {fieldErrors[subjectField.id]?.message}
                      </p>}
                  </div>

                  {/* Nội dung tin nhắn (Message) */}
                  <div className="space-y-1.5" data-index={4}>
                    <label htmlFor={messageField?.id || "f_message"} className="text-sm font-semibold text-foreground flex items-center justify-between">
                      <span>
                        Nội dung tin nhắn
                        {messageField?.required && <span className="text-destructive ml-1">*</span>}
                      </span>
                    </label>
                    <Textarea id={messageField?.id || "f_message"} rows={5} placeholder={messageField?.placeholder || "Kính gửi Ban Thư ký Hội Địa lý môi trường Việt Nam..."} value={messageField ? values[messageField.id] || "" : ""} onChange={e => messageField && handleInputChange(messageField.id, e.target.value)} disabled={isSubmitting || loadStatus === "loading"} className="bg-card border-input focus:border-primary text-foreground min-h-[120px]" />
                    {messageField && fieldErrors[messageField.id]?.message && <p className="text-xs text-destructive mt-1 flex items-center gap-1">
                        <AlertCircle className="size-3" />
                        {fieldErrors[messageField.id]?.message}
                      </p>}
                  </div>

                  {/* Any additional fields if provisioned */}
                  {otherFields.map((field, idx) => <div key={field.id} className="space-y-1.5" data-index={idx + 5}>
                      <label htmlFor={field.id} className="text-sm font-semibold text-foreground flex items-center justify-between">
                        <span>
                          {field.label}
                          {field.required && <span className="text-destructive ml-1">*</span>}
                        </span>
                      </label>
                      <Input id={field.id} type={field.type === "email" ? "email" : "text"} placeholder={field.placeholder || ""} value={values[field.id] || ""} onChange={e => handleInputChange(field.id, e.target.value)} disabled={isSubmitting || loadStatus === "loading"} className="bg-card border-input focus:border-primary text-foreground" />
                      {fieldErrors[field.id]?.message && <p className="text-xs text-destructive mt-1 flex items-center gap-1">
                          <AlertCircle className="size-3" />
                          {fieldErrors[field.id]?.message}
                        </p>}
                    </div>)}

                  {/* Submit Button */}
                  <div className="pt-2">
                    <Button type="submit" disabled={isSubmitting || loadStatus === "loading"} className="w-full h-11 bg-primary text-primary-foreground hover:bg-primary/90 rounded-md font-semibold text-sm transition-colors flex items-center justify-center gap-2 cursor-pointer shadow-sm">
                      {isSubmitting ? <>
                          <span className="inline-block size-4 border-2 border-primary-foreground border-r-transparent rounded-full animate-spin" />
                          <span>Đang gửi thư...</span>
                        </> : <>
                          <Send className="size-4" />
                          <span>Gửi liên hệ</span>
                        </>}
                    </Button>
                    <p className="text-[11px] text-muted-foreground text-center mt-2.5">
                      Thông tin gửi được bảo mật và chuyển trực tiếp tới Ban Thư ký Hội.
                    </p>
                  </div>
                </form>}
            </Card>
          </motion.div>
        </div>
      </div>
    </section>;
}